import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { API_ENDPOINTS } from '../constants/api';
import { getSignedUrls } from '../utils/fileOperations';
import { isUserAdmin } from '../utils/function';
import { reportError } from '../sentry';

function shouldSignUrl(url) {
  return typeof url === 'string' && url.trim() !== '' && !url.startsWith('http');
}

async function applySignedProfilePicturesToRooms(rooms) {
  if (!Array.isArray(rooms) || rooms.length === 0) return rooms;

  const uniqueKeys = [
    ...new Set(
      rooms
        .map((room) => room?.otherUser?.profile_picture)
        .filter(shouldSignUrl),
    ),
  ];

  if (uniqueKeys.length === 0) return rooms;

  try {
    const signedUrlsData = await getSignedUrls(uniqueKeys, true);
    if (!Array.isArray(signedUrlsData) || signedUrlsData.length === 0) return rooms;

    const signedUrlMap = signedUrlsData.reduce((acc, item) => {
      if (item?.key && item?.signedUrl) acc[item.key] = item.signedUrl;
      return acc;
    }, {});

    return rooms.map((room) => {
      const profilePicture = room?.otherUser?.profile_picture;
      const signedUrl = profilePicture ? signedUrlMap[profilePicture] : null;
      if (!signedUrl) return room;

      return {
        ...room,
        otherUser: {
          ...room.otherUser,
          profile_picture: signedUrl,
        },
      };
    });
  } catch (error) {
    reportError(error, 'signed URL profile pictures batch');
    return rooms;
  }
}

class MatrixService {
  /**
   * Register user with Matrix
   * Access token is automatically added by axios interceptor
   * @returns {Promise<Object>} Matrix registration response
   */
  async register() {
    try {
      const response = await apiClient.post(API_ENDPOINTS.MATRIX_REGISTER, {});
      return ResponseHandler.handleSuccess(response, 'Matrix registration successful');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Matrix registration failed');
    }
  }

  /**
   * Get short-lived token for Matrix
   * Access token is automatically added by axios interceptor
   * @returns {Promise<Object>} Matrix short-lived token response
   */
  async getShortLivedToken() {
    if (isUserAdmin()) {
      return {
        success: true,
        skipped: true,
        message: 'Matrix short-lived token skipped for admin users',
        data: null,
      };
    }

    try {
      const response = await apiClient.post(API_ENDPOINTS.MATRIX_SHORT_LIVED_TOKEN, {});
      return ResponseHandler.handleSuccess(response, 'Matrix short-lived token retrieved successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Matrix short-lived token retrieval failed');
    }
  }

  /**
   * Fetch Matrix rooms from a given endpoint and sign profile pictures.
   * @param {string} endpoint
   * @param {string} successMessage
   * @param {string} errorMessage
   * @returns {Promise<Object>} Matrix rooms response
   */
  async fetchRoomsFromEndpoint(endpoint, successMessage, errorMessage, { page = 1, limit = 100 } = {}) {
    try {
      const response = await apiClient.get(endpoint, { params: { page, limit } });
      const roomsData = response.data?.data;

      if (!roomsData) {
        return ResponseHandler.handleSuccess(response, successMessage);
      }

      if (!Array.isArray(roomsData.rooms)) {
        return ResponseHandler.handleSuccess(response, successMessage, roomsData);
      }

      const processedRooms = await applySignedProfilePicturesToRooms(roomsData.rooms);
      response.data.data.rooms = processedRooms;
      return ResponseHandler.handleSuccess(response, successMessage);
    } catch (error) {
      throw ResponseHandler.handleError(error, errorMessage);
    }
  }

  /**
   * Fetch Matrix rooms
   * Access token is automatically added by axios interceptor
   * @returns {Promise<Object>} Matrix rooms response
   */
  async fetchRooms({ page = 1, limit = 100 } = {}) {
    return this.fetchRoomsFromEndpoint(
      API_ENDPOINTS.MATRIX_ROOMS,
      'Matrix rooms fetched successfully',
      'Failed to fetch Matrix rooms',
      { page, limit },
    );
  }

  /**
   * Fetch all Matrix rooms (subscribers list for New Message page)
   * Access token is automatically added by axios interceptor
   * @returns {Promise<Object>} Matrix all-rooms response
   */
  async fetchAllRooms({ page = 1, limit = 100 } = {}) {
    return this.fetchRoomsFromEndpoint(
      API_ENDPOINTS.MATRIX_ALL_ROOMS,
      'Matrix all rooms fetched successfully',
      'Failed to fetch Matrix all rooms',
      { page, limit },
    );
  }

  /**
   * Mark Matrix rooms as having messages (sync backend metadata for rooms missing user info).
   * @param {string[]} roomIds - Matrix room ids
   * @returns {Promise<Object>}
   */
  async updateRoomsHasMessage(roomIds) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.MATRIX_ROOMS_UPDATE_HASMESSAGE, {
        roomIds,
      });
      return ResponseHandler.handleSuccess(response, 'Matrix rooms has-message updated successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to update Matrix rooms has-message');
    }
  }

  /**
   * Fetch Matrix room details by room id
   * Access token is automatically added by axios interceptor
   * @param {string} userId - User id
   * @returns {Promise<Object>} Matrix room response
   */
  async createOrGetMatrixRoom(userId) {
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.MATRIX_CREATE_OR_GET_ROOM}/${userId}`, {});
      return ResponseHandler.handleSuccess(response, 'Matrix room created or fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to create or get Matrix room');
    }
  }

  /**
   * Fetch Matrix room info
   * Access token is automatically added by axios interceptor
   * @param {string} userId - User id from route param
   * @returns {Promise<Object>} Matrix room info response
   */
  async getMatrixRoomInfo(userId) {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.MATRIX_ROOM_INFO}/${userId}`);
      const roomInfo = response.data?.data;

      const profilePicture = roomInfo?.destUser?.profile_picture;
      if (shouldSignUrl(profilePicture)) {
        try {
          const signedUrlsData = await getSignedUrls([profilePicture], true);
          if (Array.isArray(signedUrlsData) && signedUrlsData.length > 0) {
            response.data.data.destUser.profile_picture = signedUrlsData[0].signedUrl;
          }
        } catch (error) {
          reportError(error, 'signed URL profile picture');
          // Keep the original value if signed URL fetch fails
        }
      }

      const profileBanner = roomInfo?.destUser?.profile_banner;
      if (shouldSignUrl(profileBanner)) {
        try {
          const signedUrlsData = await getSignedUrls([profileBanner], true);
          if (Array.isArray(signedUrlsData) && signedUrlsData.length > 0) {
            response.data.data.destUser.profile_banner = signedUrlsData[0].signedUrl;
          }
        } catch (error) {
          reportError(error, 'signed URL profile banner');
          // Keep the original value if signed URL fetch fails
        }
      }

      return ResponseHandler.handleSuccess(response, 'Matrix room info fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch Matrix room info');
    }
  }

  /**
   * Delete Matrix room by room id
   * Access token is automatically added by axios interceptor
   * @param {string} roomId - Matrix room id
   * @returns {Promise<Object>} Delete room response
   */
  async deleteMatrixRoom(roomId) {
    try {
      const encodedRoomId = encodeURIComponent(roomId);
      const response = await apiClient.delete(`${API_ENDPOINTS.MATRIX_DELETE_ROOM}/${encodedRoomId}`);
      return ResponseHandler.handleSuccess(response, 'Matrix room deleted successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to delete Matrix room');
    }
  }

  /**
   * Leave Matrix room by room id
   * Access token is automatically added by axios interceptor
   * @param {string} roomId - Matrix room id
   * @returns {Promise<Object>} Leave room response
   */
  async leaveMatrixRoom(roomId) {
    try {
      // NOTE: matrix room IDs contain characters like ":" and "!" which are valid
      // in path segments. We keep it unencoded to match the expected URL shape.
      const response = await apiClient.delete(`${API_ENDPOINTS.MATRIX_LEAVE_ROOM}/${roomId}`, {});
      return ResponseHandler.handleSuccess(response, 'Matrix room left successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to leave Matrix room');
    }
  }

  /**
   * Fetch restricted words for messages
   * Access token is automatically added by axios interceptor
   * @returns {Promise<Object>} Restricted words response
   */
  async getRestrictedWords() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.MESSAGES_RESTRICTED_WORD);
      return ResponseHandler.handleSuccess(response, 'Restricted words fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch restricted words');
    }
  }

  /**
   * Reply to message tips (creator replying to subscriber's tip message)
   * Access token is automatically added by axios interceptor
   * @param {string} subscriberUserId - Subscriber (other user) id
   * @param {string} creatorMessage - Creator reply message
   * @returns {Promise<Object>} Reply response
   */
  async replyToMessageTips(subscriberUserId, creatorMessage) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.MESSAGE_TIPS_REPLY, {
        subscriberUserId,
        creatorMessage
      });
      return ResponseHandler.handleSuccess(response, 'Message tips reply successful');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to reply to message tips');
    }
  }

  /**
   * Attach Matrix event id to a message tip
   * Access token is automatically added by axios interceptor
   * @param {{ matrixEventId: string, tipId: string }} payload
   * @returns {Promise<Object>} Response with success, data and message
   */
  async addMatrixIdForMessageTip({ matrixEventId, tipId }) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.MESSAGE_TIPS_ADD_MATRIX_ID, {
        matrixEventId,
        tipId,
      });
      return ResponseHandler.handleSuccess(
        response,
        'Matrix id added for message tip successfully'
      );
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to add Matrix id for message tip');
    }
  }

  /**
   * Notify a user about a new chat message (push/email). Used after Matrix send succeeds.
   * @param {{
   *   recipientUserId: string,
   *   previewText: string,
   *   isPriority?: boolean,
   *   senderUserRole: number,
   *   receiverUserRole: number,
   * }} payload
   * @returns {Promise<Object>} Notify response
   */
  async notifyMessage(payload) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.USER_MESSAGE_NOTIFY, payload);
      return ResponseHandler.handleSuccess(response, 'Message notify sent successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to send message notify');
    }
  }
}

export default new MatrixService();
